/**
 * Artefact Card Height Utilities
 *
 * Manages the expanded artefact card height with localStorage persistence.
 * One shared height applies to every expanded card. Mirrors the pattern in
 * panelWidth.ts and promptHeight.ts.
 */

import { logger } from "@/telemetry";

export const ARTEFACT_HEIGHT_KEY = "artefactExpandedHeight";
export const DEFAULT_ARTEFACT_HEIGHT = 400; // px
export const MIN_ARTEFACT_HEIGHT = 200;
export const MAX_ARTEFACT_HEIGHT_RATIO = 0.8; // 80% of viewport height

/**
 * Below this card height the artefact header switches to its compact form.
 *
 * The header is a frozen band rather than part of the scrolling content, so its
 * cost comes straight off the readable area instead of scrolling away. At full
 * size it spends roughly 80px on the icon tile, a title clamped to two lines at
 * `text-lg`, the metadata row and its gutter. Against `MIN_ARTEFACT_HEIGHT` of
 * 200, less the 32px the list spends on `p-4`, that leaves about 50px of content
 * once the scroller's own padding is taken, which is not enough to read.
 */
export const COMPACT_ARTEFACT_HEIGHT = 280;

/**
 * Get the initial artefact card height from localStorage.
 * Returns the default height if no valid value is saved.
 */
export function getInitialArtefactHeight(): number {
  try {
    const saved = localStorage.getItem(ARTEFACT_HEIGHT_KEY);

    if (saved) {
      const height = parseFloat(saved);

      if (height >= MIN_ARTEFACT_HEIGHT) {
        return height;
      }
    }
  } catch (error) {
    logger.warn("Failed to load saved artefact height:", { error: String(error) });
  }

  return DEFAULT_ARTEFACT_HEIGHT;
}

/**
 * Save the artefact card height to localStorage.
 */
export function saveArtefactHeight(height: number): void {
  try {
    localStorage.setItem(ARTEFACT_HEIGHT_KEY, height.toString());
  } catch (error) {
    logger.warn("Failed to save artefact height:", { error: String(error) });
  }
}

/**
 * Constrain a height value to the valid range based on viewport height.
 */
export function constrainArtefactHeight(height: number, viewportHeight: number): number {
  const maxHeight = viewportHeight * MAX_ARTEFACT_HEIGHT_RATIO;
  // Ensure min never exceeds max on very small viewports
  const effectiveMin = Math.min(MIN_ARTEFACT_HEIGHT, maxHeight);

  return Math.max(effectiveMin, Math.min(maxHeight, height));
}
